import { Control } from '@yleisradio/areena-types';
import { BackLink } from 'components/AlternateButton/BackLink';
import {
  AddToQueueButton,
  Button,
  ButtonPadding,
  ButtonSize,
  ButtonVariant,
  PlayButton,
} from 'components/Button';
import { SeriesLink } from 'components/Button/SeriesLink';
import {
  ControlLink,
  DSLink,
  LinkIconPosition,
  LinkTextSize,
  LinkTheme,
} from 'components/DSLink';
import { isNavigator } from './typeGuards';
import { useIcon } from './useIcon';

export type ControlPresentationType = 'button' | 'link';

type ControlPresentationProps = {
  'aria-controls'?: string | undefined;
  'aria-expanded'?: boolean | undefined;
  as: ControlPresentationType;
  buttonPadding?: ButtonPadding | undefined;
  buttonSize?: ButtonSize | undefined;
  buttonVariant?: ButtonVariant | undefined;
  width?: 'full' | undefined;
  control: Control;
  disabled?: boolean;
  executingAction?: boolean;
  onClick?: () => void;
  textSize?: LinkTextSize | undefined;
  iconPosition?: LinkIconPosition | undefined;
  theme?: LinkTheme | undefined;
  dataTestId?: string | undefined;
};

export const ControlPresentation: React.FunctionComponent<
  ControlPresentationProps
> = (props) => {
  const {
    control,
    disabled = false,
    onClick,
    textSize,
    iconPosition,
    theme,
    dataTestId,
  } = props;
  const Icon = useIcon(control);

  const controlDataTestid =
    dataTestId || (control.icon?.id && `control-${control.icon.id}`);

  if (control.tag === 'cookie-consent') {
    return (
      <DSLink
        text={control.title}
        textSize="s"
        pointer={null}
        onClick={onClick}
        theme="light"
        dataTestId={controlDataTestid}
      />
    );
  }

  if (props.as === 'link') {
    return (
      <ControlLink
        control={control}
        onClick={onClick}
        textSize={textSize}
        iconPosition={iconPosition}
        theme={theme}
        dataTestId={controlDataTestid}
      />
    );
  }

  const {
    buttonPadding: padding,
    buttonSize: size,
    buttonVariant: variant,
    width: width,
  } = props;

  if (control.tag === 'play') {
    return (
      <PlayButton
        control={control}
        disabled={disabled}
        onClick={onClick}
        padding={padding}
        size={size}
        variant={variant}
        width={width}
        dataTestId={controlDataTestid}
      />
    );
  }

  if (control.tag === 'series') {
    return (
      <SeriesLink
        control={control}
        disabled={disabled}
        onClick={onClick}
        dataTestId={controlDataTestid}
      />
    );
  }

  if (control.tag === 'back') {
    return (
      <BackLink
        control={control}
        disabled={disabled}
        onClick={onClick}
        dataTestId={controlDataTestid}
      />
    );
  }

  if (control.tag === 'queue') {
    return (
      <AddToQueueButton
        control={control}
        disabled={disabled}
        onClick={onClick}
        padding={padding}
        size={size}
        variant={variant}
      />
    );
  }

  return (
    <Button
      text={control.title || ''}
      disabled={disabled}
      download={control.tag === 'downloadLink'}
      iconLeft={Icon}
      onClick={onClick}
      padding={padding}
      pointer={isNavigator(control) ? control.destination : undefined}
      size={size}
      variant={variant}
      theme={theme}
      aria-expanded={props['aria-expanded']}
      aria-controls={props['aria-controls']}
      dataTestId={controlDataTestid}
    />
  );
};
